const SHAPE_METADATA_FIELDS = ['createdByUsername', 'createdAt', 'updatedByUsername', 'updatedAt'];
const SHAPE_MEASUREMENT_FIELDS = ['displayMeasurement', 'displayTotalMeasurement', 'displayAreaMeasurement', 'displaySegmentMeasurements'];

const removeUndefinedValues = (obj) => {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(removeUndefinedValues).filter(val => val !== undefined);
  }
  
  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      const cleanedValue = removeUndefinedValues(value);
      if (cleanedValue !== undefined) {
        cleaned[key] = cleanedValue;
      }
    }
  }
  return cleaned;
};

const COMPACT_FIELD_MAP = {
  type: 't',
  points: 'pts',
  holes: 'hl',
  stroke: 'sc',
  strokeWidth: 'sw',
  fill: 'fl',
  opacity: 'op',
  rotation: 'r',
  scaleX: 'sx',
  scaleY: 'sy',
  text: 'tx',
  fontSize: 'fs',
  fontFamily: 'ff',
  textOffsetX: 'tox',
  textOffsetY: 'toy',
  params: 'p',
  originalVersion: 'ov',
  lastModifiedVersion: 'lmv',
  originalDPI: 'odpi',
  storedDPI: 'sdpi',
  scaleKey: 'sk',
  createdByUsername: 'cbu',
  createdAt: 'ca',
  updatedByUsername: 'ubu',
  updatedAt: 'ua',
  visible: 'v',
  locked: 'l',
  distance: 'd',
  offsetDist: 'od',
  pointMarker: 'pm',
  radius: 'rd',
  sourceOperation: 'so',
  sourcePolygons: 'sp',
  isAxisAlignedRectangle: 'iar',
  isRectangleMode: 'irm',
  previewWidth: 'pw',
  previewHeight: 'ph',
  rectangleStart: 'rs',
  color: 'c',
  width: 'w',
  height: 'h',
};

const EXPAND_FIELD_MAP = Object.fromEntries(
  Object.entries(COMPACT_FIELD_MAP).map(([k, v]) => [v, k])
);

const DEFAULT_CANDIDATES = ['createdByUsername', 'createdAt', 'originalDPI'];

export const normalizeShapeHolesFromFirestore = (shape) => {
  if (!shape || typeof shape !== 'object') return shape;

  let normalized = shape;
  if (shape.type === 'POLYGON') {
    normalized = { ...shape, type: 'AREA' };
  }

  if (
    normalized.holes &&
    Array.isArray(normalized.holes) &&
    normalized.holes.length > 0 &&
    typeof normalized.holes[0] === 'object' &&
    normalized.holes[0] !== null &&
    'points' in normalized.holes[0]
  ) {
    return { ...normalized, holes: normalized.holes.map(holeObj => holeObj.points) };
  }

  return normalized;
};

export const sanitizeShapeForFirestore = (shape) => {
  if (!shape || typeof shape !== 'object') return shape;

  let sanitized = { ...shape };
  SHAPE_MEASUREMENT_FIELDS.forEach(field => {
    delete sanitized[field];
  });

  delete sanitized.segmentDistances;
  delete sanitized.totalDistance;
  delete sanitized.area;
  delete sanitized.centroid;
  
  delete sanitized._measurementCacheKey;
  delete sanitized.pendingTextEdit;

  if (
    sanitized.holes &&
    Array.isArray(sanitized.holes) &&
    sanitized.holes.length > 0 &&
    Array.isArray(sanitized.holes[0])
  ) {
    sanitized = {
      ...sanitized,
      holes: sanitized.holes.map(holePoints => ({ points: holePoints })),
    };
  }

  return removeUndefinedValues(sanitized);
};

export const extractShapeDefaults = (shapes) => {
  if (!shapes || shapes.length === 0) return {};
  const defaults = {};
  for (const key of DEFAULT_CANDIDATES) {
    const val = shapes[0][key];
    if (val !== undefined && shapes.every(s => s[key] === val)) {
      defaults[COMPACT_FIELD_MAP[key] || key] = val;
    }
  }
  return defaults;
};

export const compactShape = (shape, defaults = {}) => {
  const result = {};
  for (const [key, value] of Object.entries(shape)) {
    if (key === 'id') continue;
    const compactKey = COMPACT_FIELD_MAP[key] || key;
    if (compactKey in defaults && value === defaults[compactKey]) continue;
    result[compactKey] = value;
  }
  return removeUndefinedValues(result);
};

export const expandShape = (id, compactData, defaults = {}) => {
  const result = { id };
  for (let [key, value] of Object.entries(defaults)) {
    result[EXPAND_FIELD_MAP[key] || key] = value;
  }
  for (let [key, value] of Object.entries(compactData)) {
    result[EXPAND_FIELD_MAP[key] || key] = value;
  }
  return result;
};

export const getOrderedShapesFromPageData = (pageData) => {
  if (!pageData || typeof pageData !== 'object') return [];

  const shapeDefaults = pageData.shapeDefaults || {};
  const shapesById = pageData.shapesById && typeof pageData.shapesById === 'object' ? pageData.shapesById : null;
  const shapeOrder = Array.isArray(pageData.shapeOrder) ? pageData.shapeOrder : [];

  if (shapesById) {
    const getExpanded = (shapeId) => {
      const raw = shapesById[shapeId];
      if (!raw) return null;
      return normalizeShapeHolesFromFirestore(expandShape(shapeId, raw, shapeDefaults));
    };

    const shapesFromOrder = shapeOrder.map(getExpanded).filter(Boolean);
    const orderSet = new Set(shapeOrder);
    const unorderedShapes = Object.keys(shapesById)
      .filter(shapeId => !orderSet.has(shapeId))
      .map(getExpanded)
      .filter(Boolean);

    return [...shapesFromOrder, ...unorderedShapes];
  }

  return (pageData.shapes || []).map(normalizeShapeHolesFromFirestore);
};

export const normalizeShapeForDiff = (shape) => {
  if (!shape || typeof shape !== 'object') return shape;

  const normalized = { ...shape };
  [...SHAPE_METADATA_FIELDS, ...SHAPE_MEASUREMENT_FIELDS].forEach((field) => {
    if (field in normalized) {
      delete normalized[field];
    }
  });

  return normalized;
};

export const areShapesEquivalentForSaveDiff = (a, b) => {
  return JSON.stringify(normalizeShapeForDiff(a)) === JSON.stringify(normalizeShapeForDiff(b));
};
